<script setup lang="ts">
import type { SiteSectionsTreeSectionCategoryFilterOption } from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.types";
import type { SiteSectionCategoryId } from "@/data/common/ids";

import { cn, normalizeForSearch } from "@voltalis/frontend-utils";
import { PlusIcon } from "@voltalis/ui-kit/icons";
import { onClickOutside } from "@vueuse/core";
import { computed, ref } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import InputText from "primevue/inputtext";
import Popover from "primevue/popover";

import CategoryCreationForm from "@/components/tools/site-section/category-creation/category-creation-form.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const props = defineProps<{
  mode: "multiple" | "single";
  options: readonly SiteSectionsTreeSectionCategoryFilterOption[];
  createCategory: (name: string) => Promise<boolean>;
  isCreatingCategory?: boolean;
  selectCategory?: (categoryId: SiteSectionCategoryId) => Promise<boolean> | boolean;
}>();

const selectedCategories = defineModel<SiteSectionCategoryId[]>("selectedCategories", { default: () => [] });

const { t, k } = useTypedIntl();

const popover = ref<InstanceType<typeof Popover>>();
const searchQuery = ref<string>("");
const isOpen = ref<boolean>(false);
const isCategoryCreationFormVisible = ref<boolean>(false);
const popoverContent = ref<HTMLElement>();
const trigger = ref<HTMLElement>();
const popoverTargetHalfWidth = ref<string>("var(--p-popover-gutter)");

const selectedCategoryCount = computed<number>(() => {
  return props.options.filter((option) => selectedCategories.value.includes(option.id)).length;
});
const hasSelectedCategories = computed<boolean>(() => selectedCategoryCount.value > 0);
const filteredOptions = computed<SiteSectionsTreeSectionCategoryFilterOption[]>(() => {
  const normalizedQuery = normalizeForSearch(searchQuery.value.trim());

  if (!normalizedQuery) return [...props.options];

  return props.options.filter((option) => normalizeForSearch(option.label).includes(normalizedQuery));
});

function toggle(event: Event) {
  if (event.currentTarget instanceof HTMLElement) {
    popoverTargetHalfWidth.value = `${event.currentTarget.offsetWidth / 2}px`;
  }

  popover.value?.toggle(event);
}

function hide() {
  popover.value?.hide();
}

function startCategoryCreation() {
  isCategoryCreationFormVisible.value = true;
}

function resetCategoryCreationForm() {
  isCategoryCreationFormVisible.value = false;
}

function resetPopoverState() {
  searchQuery.value = "";
  resetCategoryCreationForm();
}

function handleShow() {
  isOpen.value = true;
}

function handleHide() {
  isOpen.value = false;
  resetPopoverState();
}

onClickOutside(popoverContent, hide, { ignore: [trigger] });

function toggleCategory(categoryId: SiteSectionCategoryId) {
  if (selectedCategories.value.includes(categoryId)) {
    selectedCategories.value = selectedCategories.value.filter((id) => id !== categoryId);
    return;
  }

  selectedCategories.value = [...selectedCategories.value, categoryId];
}

function isCategorySelected(categoryId: SiteSectionCategoryId): boolean {
  return selectedCategories.value.includes(categoryId);
}

async function selectSingleCategory(categoryId: SiteSectionCategoryId) {
  if (!props.selectCategory) return;

  const shouldClose = await props.selectCategory(categoryId);

  if (shouldClose !== false) {
    hide();
  }
}

async function confirmCategoryCreation(categoryName: string) {
  try {
    const wasCategoryCreated = await props.createCategory(categoryName);

    if (wasCategoryCreated) {
      resetCategoryCreationForm();
    }
  } catch {
    // The parent handles toast notifications; keep the form open so the user can retry.
  }
}
</script>

<template>
  <span
    ref="trigger"
    class="contents"
  >
    <slot
      name="trigger"
      :toggle="toggle"
      :is-open="isOpen"
      :has-selected-categories="hasSelectedCategories"
      :selected-category-count="selectedCategoryCount"
    />
  </span>

  <Popover
    ref="popover"
    :pt="{
      root: {
        class: 'site-sections-tree-section-category-selector',
        'data-test-id': DATA_TEST_IDS.components.siteSectionsTreeSectionCategoryFilter.popover,
        style: {
          '--p-popover-target-half-width': popoverTargetHalfWidth,
        },
      },
    }"
    @show="handleShow"
    @hide="handleHide"
  >
    <div
      ref="popoverContent"
      class="flex w-[280px] flex-col gap-3 p-3"
    >
      <span class="relative flex items-center">
        <i class="pi pi-search pointer-events-none absolute left-3 text-xs text-tertiary" />
        <InputText
          v-model="searchQuery"
          class="!h-[38px] w-full !rounded-[8px] !border-gray-200 !py-2 !pl-9 !pr-3 !text-sm"
          :placeholder="t(k.tools.siteManagement.siteTreeStructure.searchCategory)"
          :data-test-id="DATA_TEST_IDS.components.siteSectionsTreeSectionCategoryFilter.searchInput"
        />
      </span>

      <div
        :class="
          cn('flex max-h-[240px] flex-col overflow-y-auto', {
            'pointer-events-none opacity-40': isCategoryCreationFormVisible,
          })
        "
      >
        <button
          v-for="option in filteredOptions"
          :key="option.id"
          type="button"
          :class="
            cn('flex min-h-[38px] w-full items-center gap-3 rounded-[6px] px-2 py-2 text-left transition-colors', {
              'bg-blue-100/30 hover:bg-blue-100/30': isCategorySelected(option.id),
              'hover:bg-blue-100/30': !isCategorySelected(option.id),
            })
          "
          :data-test-id="DATA_TEST_IDS.components.siteSectionsTreeSectionCategoryFilter.option(option.id)"
          @click="props.mode === 'multiple' ? toggleCategory(option.id) : selectSingleCategory(option.id)"
        >
          <i
            v-if="props.mode === 'single' && isCategorySelected(option.id)"
            class="pi pi-check shrink-0 text-sm text-brand"
          />

          <span
            v-if="props.mode === 'multiple'"
            :class="
              cn('flex size-4 shrink-0 items-center justify-center rounded border border-gray-300', {
                'border-brand bg-brand text-white': isCategorySelected(option.id),
              })
            "
            :data-test-id="DATA_TEST_IDS.components.siteSectionsTreeSectionCategoryFilter.checkbox(option.id)"
          >
            <i
              v-if="isCategorySelected(option.id)"
              class="pi pi-check !text-[10px]"
            />
          </span>

          <span
            :class="
              cn('min-w-0 flex-1 truncate text-sm font-semibold text-text-secondary', {
                'text-brand': isCategorySelected(option.id),
              })
            "
            >{{ option.label }}</span
          >
        </button>

        <span
          v-if="filteredOptions.length === 0"
          class="px-2 py-3 text-sm text-tertiary"
        >
          {{ t(k.global.misc.noResult) }}
        </span>
      </div>

      <div class="border-t pt-3">
        <template v-if="isCategoryCreationFormVisible">
          <CategoryCreationForm
            :is-loading="props.isCreatingCategory"
            @confirm="confirmCategoryCreation"
            @cancel="resetCategoryCreationForm"
          />
        </template>

        <HospitalityButton
          v-else
          :data-test-id="DATA_TEST_IDS.components.siteSectionsTreeSectionCategoryFilter.createButton"
          :icon="PlusIcon"
          severity="link"
          size="xs"
          @click="startCategoryCreation"
        >
          {{ t(k.tools.siteManagement.siteTreeStructure.createCategory) }}
        </HospitalityButton>
      </div>
    </div>
  </Popover>
</template>
